/* Loading PayPal's JS SDK, once, for one client ID and one currency.

   Copied in shape from `utils/googleIdentity.js`, and for the same reasons. The buttons have to be
   drawn by PayPal's own script — what the player types into them never touches our page, which is
   the entire point of using PayPal rather than taking a card number — so there is nothing to
   reimplement. It is loaded on demand rather than from `public/index.html` because it is needed by
   one modal and by nobody who never opens it: a player looking at the map should not be fetching
   paypal.com.

   Two things differ from the Google loader, and both come from PayPal's SDK taking its
   configuration in the script URL rather than in an init call:

   - the cache is keyed by client ID *and* currency. `window.paypal` is whatever the last loaded
     URL produced, so a second load with a different currency would silently be ignored and the
     buttons would charge in the first one.
   - `components=buttons` and `intent=capture` are pinned in the URL. The default bundle pulls in
     hosted fields and messaging we do not use, and `intent` has to match what the server created
     the order with or PayPal refuses the capture. */

export const PAYPAL_SDK_ORIGIN = "https://www.paypal.com/sdk/js"

// One entry per (clientId, currency). Not cleared on success: `window.paypal` is global state and
// re-loading the same configuration would replace the namespace under any button already rendered.
const pending = new Map()

const cacheKey = (clientId, currency) => `${clientId}|${currency}`

export const paypalSdkUrl = (clientId, currency) =>
  `${PAYPAL_SDK_ORIGIN}?client-id=${encodeURIComponent(clientId)}&currency=${encodeURIComponent(
    currency
  )}&components=buttons&intent=capture`

export const loadPayPalSdk = (clientId, currency = "EUR") => {
  // No client ID means this server sells nothing. Rejecting here rather than loading a script that
  // cannot work keeps the failure at the place that can explain it — the shop hides its checkout
  // and says so, instead of the player watching a button never appear.
  if (!clientId) {
    return Promise.reject(new Error("This server has no PayPal client ID configured"))
  }

  const key = cacheKey(clientId, currency)
  if (pending.has(key)) return pending.get(key)

  const promise = new Promise((resolve, reject) => {
    const url = paypalSdkUrl(clientId, currency)

    // A tag already in the page without `window.paypal` beside it is a load that failed, not one
    // to wait on: its `load` event has been and gone, so listening for it would hang for ever.
    document.querySelectorAll(`script[src="${url}"]`).forEach((tag) => tag.remove())

    const script = document.createElement("script")
    const giveUp = (message) => {
      // Cleared so a later attempt can retry. An ad blocker or an offline moment is not permanent,
      // and a cached rejected promise would keep the shop broken until a reload.
      pending.delete(key)
      script.remove()
      reject(new Error(message))
    }

    script.addEventListener("load", () => {
      if (window.paypal) {
        resolve(window.paypal)
      } else {
        // Loaded, and the namespace is not there — a blocked or rewritten response. Failing beats
        // resolving with undefined and letting the caller crash on `.Buttons`.
        giveUp("The PayPal SDK loaded without its namespace")
      }
    })
    script.addEventListener("error", () => giveUp("The PayPal SDK could not be loaded"))

    script.src = url
    script.async = true
    document.head.appendChild(script)
  })

  pending.set(key, promise)
  return promise
}

// Tests mount and unmount the checkout; without this the module-level cache leaks between them.
export const resetPayPalSdkForTests = () => {
  pending.clear()
}

export default loadPayPalSdk
